import {
	DialogContent,
	type DialogContentPrimitiveProps,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../dialog';
import type { ButtonProps } from '@components/molecules';
import { Button, ButtonSize, ButtonVariant, BackButton } from '@components/molecules';
import { cn, type CompositionProp } from '@utils';

type DialogPrimaryLayoutProps = React.PropsWithChildren<
	{
		icon?: React.ReactNode;
		title: CompositionProp;
		description?: CompositionProp;
		footerDescription?: CompositionProp;
		primaryButton?: CompositionProp;
		secondaryButton?: CompositionProp;
		primaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
		secondaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
		onPrimaryAction?: () => void;
		onSecondaryAction?: () => void;
		onBackAction?: () => void;
		buttonsContainerClassName?: string;
		closeIcon?: boolean;
		sectionDivider?: boolean;
		footerProps?: React.HTMLAttributes<HTMLDivElement>;
	} & DialogContentPrimitiveProps
>;

export const DialogPrimaryLayout: React.FC<DialogPrimaryLayoutProps> = ({
	icon,
	title,
	description,
	className,
	children,
	footerDescription,
	primaryButton,
	secondaryButton,
	primaryButtonProps,
	secondaryButtonProps,
	onPrimaryAction,
	onSecondaryAction,
	onBackAction,
	closeIcon = true,
	sectionDivider = true,
	footerProps,
	buttonsContainerClassName,
	...props
}) => {
	const titleComponent = typeof title === 'string' ? <DialogTitle>{title}</DialogTitle> : (title as React.ReactNode);

	const descriptionComponent =
		typeof description === 'string' ? (
			<DialogDescription>{description}</DialogDescription>
		) : (
			(description as React.ReactNode)
		);

	const footerDescriptionComponent =
		typeof footerDescription === 'string' ? (
			<p className="text-sm text-secondary">{footerDescription}</p>
		) : (
			(footerDescription as React.ReactNode)
		);

	const primaryButtonComponent =
		typeof primaryButton === 'string' ? (
			<Button
				{...primaryButtonProps}
				size={ButtonSize.SM}
				type="button"
				onClick={onPrimaryAction}
				className="uppercase"
			>
				{primaryButton}
			</Button>
		) : (
			(primaryButton as React.ReactNode)
		);

	const secondaryButtonComponent =
		typeof secondaryButton === 'string' ? (
			<Button
				{...secondaryButtonProps}
				type="button"
				variant={ButtonVariant.SECONDARY}
				size={ButtonSize.SM}
				onClick={onSecondaryAction}
			>
				{secondaryButton}
			</Button>
		) : (
			(secondaryButton as React.ReactNode)
		);

	return (
		<DialogContent
			backButtonComponent={onBackAction ? <BackButton onAction={onBackAction}>Back</BackButton> : undefined}
			closeIcon={closeIcon}
			className={cn('max-h-[90vh] min-h-0 w-[520px] sm:max-w-[95vw]', className)}
			{...props}
		>
			<div
				className={cn(
					'flex max-h-[calc(90vh-2rem)] min-h-0 flex-col overflow-hidden',
					sectionDivider ? 'divide-y divide-borderSeparator' : ''
				)}
			>
				<DialogHeader className="shrink-0 grow-0 justify-items-center pb-4">
					<div className="self-center">{icon && icon}</div>
					{titleComponent}
					{descriptionComponent}
				</DialogHeader>
				<div className="min-h-0 flex-1 overflow-y-auto px-10 pt-4 pb-4">{children}</div>
				<DialogFooter className="shrink-0 grow-0 justify-between" {...footerProps}>
					<div className="content-center">{footerDescription && footerDescriptionComponent}</div>
					<div className={cn('flex flex-row space-x-4', buttonsContainerClassName)}>
						{onSecondaryAction && secondaryButtonComponent}
						{primaryButton && primaryButtonComponent}
					</div>
				</DialogFooter>
			</div>
		</DialogContent>
	);
};
